import { computed, reactive, ref, watch } from 'vue';
import type { ScreenSummary } from '@codot/contracts';
import { allPlayers, screensByIds } from '../api/screens.api';
import { t } from '../i18n';
import { toast, type ToastAction } from './toast';

/**
 * Sync tray: after a publish, follow every player it changed until each one is PLAYING the new content version
 * (not just sent it). Kept in this browser (survives reloads); polls the players list every 3 s while any is open.
 */
export type PlayerSync = 'done' | 'partial' | 'downloading' | 'waiting' | 'failing' | 'offline' | 'problem';
export interface RolloutPlayer {
  screenId: string;
  name: string;
  revision: number;
  /** Followed after a change (remove, pause, reorder…), not a publish: no new files are expected. */
  change?: boolean;
  /** Not published to: it follows this lead in a synchronized group, so it gets the new content too. */
  syncedWith?: string;
  /** A device was just paired (Date.now()): it needs a moment to connect, so "offline" reads as waiting at first. */
  pairedAt?: number;
}
export interface Rollout {
  id: string;
  label: string;
  startedAt: number;
  players: RolloutPlayer[];
  /** When every player got there (the green state fades out a little later). */
  doneAt?: number;
  /** When every player left to update was offline: nothing more to watch now (they update when back). */
  parkedAt?: number;
  /** Just started: if every player turns out to be offline, a notice replaces the tray (checked on the first poll). */
  fresh?: boolean;
}

/**
 * What was done ("Published to 1 player", "Unpublished 2 items", with Undo): shown once the first check knows whether
 * anyone is online — on its own, or as the start of the offline notice. Kept in memory (it can hold an Undo function).
 */
export interface Confirm {
  message: string;
  tone?: 'success' | 'info';
  action?: ToastAction;
}
const confirms = new Map<string, Confirm>();
function showConfirm(c: Confirm | undefined): void {
  if (c) toast(c.message, { tone: c.tone ?? 'success', ...(c.action ? { action: c.action } : {}) });
}

const KEY = 'codot.rollouts';
const MAX_AGE_MS = 24 * 60 * 60_000;
function load(): Rollout[] {
  try {
    const list = JSON.parse(localStorage.getItem(KEY) ?? '[]') as Rollout[];
    return Array.isArray(list) ? list.filter((r) => Date.now() - r.startedAt < MAX_AGE_MS) : [];
  } catch {
    return [];
  }
}
export const rollouts = reactive<Rollout[]>(load());
watch(rollouts, (v) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(v));
  } catch {
    /* private window: the tray just won't survive a reload */
  }
});

/** Latest status of the players being followed (from the poll). */
export const screensById = ref(new Map<string, ScreenSummary>());

export function startRollout(label: string, res: { players?: RolloutPlayer[] }, confirm?: Confirm): void {
  if (!res.players?.length) return showConfirm(confirm);
  // The same players again (several edits in a row): one row that follows the newest version, not one per change.
  const same = (r: Rollout) =>
    r.players.length === res.players!.length && res.players!.every((p) => r.players.some((q) => q.screenId === p.screenId));
  const prev = rollouts.findIndex(same);
  const players =
    prev < 0
      ? res.players
      : res.players.map((p) => {
          const q = rollouts[prev]!.players.find((x) => x.screenId === p.screenId)!;
          // A publish still downloading stays a download even if a later change follows it.
          return { ...q, ...p, revision: Math.max(p.revision, q.revision), change: !!p.change && !!q.change };
        });
  if (prev >= 0) {
    confirms.delete(rollouts[prev]!.id);
    rollouts.splice(prev, 1);
  }
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  if (confirm) confirms.set(id, confirm);
  rollouts.unshift({ id, label, startedAt: Date.now(), players, fresh: true });
  if (rollouts.length > 5) rollouts.splice(5);
  void poll();
}
/**
 * Any other change to what a player (or a group's players) plays — remove, pause, reorder, display settings — also
 * sends them a new version: follow it the same way. The target version is each player's latest one right now.
 */
type Scope = { kind: 'player' | 'group' | 'tag'; id: string };
/** Several places changed at once (e.g. unpublished from 2 players and a group): one follow, one message. */
export async function followChange(label: string, scope: Scope | Scope[], confirm?: Confirm): Promise<void> {
  try {
    const scopes = Array.isArray(scope) ? scope : [scope];
    const ids = scopes.filter((x) => x.kind === 'player').map((x) => x.id);
    const lists = await Promise.all([
      ids.length ? screensByIds(ids) : Promise.resolve([]),
      ...scopes.filter((x) => x.kind !== 'player').map((x) => allPlayers(x.kind === 'group' ? { group: x.id } : { tag: x.id })),
    ]);
    const list = [...new Map(lists.flat().map((p) => [p.id, p])).values()];
    const players = list.flatMap((s) => {
      const revision = s.content?.latestRevision ?? s.manifestRevision;
      // Already playing it (nothing changed for this player): nothing to follow.
      return revision && (s.content?.playingRevision ?? 0) < revision
        ? [{ screenId: s.id, name: s.name, revision, change: true }]
        : [];
    });
    startRollout(label, { players }, confirm);
  } catch {
    showConfirm(confirm); // the tray is a hint: the change itself already succeeded
  }
}

/** How long a newly paired device may take to connect before the tray calls it offline. */
const PAIRING_GRACE_MS = 2 * 60_000;
/**
 * A new device paired to a player that already has content downloads all of it: follow that like a publish
 * (nothing to follow when the player has nothing published).
 */
export function followPairing(label: string, s: ScreenSummary): void {
  const revision = s.content?.latestRevision;
  if (!revision || s.content?.status === 'none') return;
  startRollout(label, { players: [{ screenId: s.id, name: s.name, revision, pairedAt: Date.now() }] });
}

/** "Unpublished 1 item" → "Unpublished 1 item." (so the next sentence reads on). */
const sentence = (m: string) => (/[.!?…]$/.test(m.trim()) ? m.trim() : `${m.trim()}.`);
function offlineNotice(r: Rollout, done?: Confirm): void {
  const n = r.players.length;
  const one = n === 1 ? r.players[0]! : null;
  const changed = r.players.every((p) => p.change);
  // The confirmation already names the player ("Web player test has nothing to play now…"): "It's offline…", not the name again.
  const named = !!one && !!done?.message.includes(one.name);
  const offline = one
    ? named
      ? t(changed ? 'sync.offlineChangedIt' : 'sync.offlineIt')
      : t(changed ? 'sync.offlineChangedOne' : 'sync.offlineOne', { name: one.name })
    : t(changed ? 'sync.offlineChangedMany' : 'sync.offlineMany', { n }, n);
  toast(done ? `${sentence(done.message)} ${offline}` : offline, {
    tone: 'info',
    ms: 10_000,
    // Undo (what was done) comes first; otherwise a way to the player.
    ...(done?.action
      ? { action: done.action }
      : one
        ? {
            action: {
              label: t('sync.viewPlayer'),
              run: () =>
                void import('../router').then(({ router }) =>
                  router.push({ name: 'screen-detail', params: { screenId: one.screenId } }),
                ),
            },
          }
        : {}),
  });
}

export function dismissRollout(id: string): void {
  const i = rollouts.findIndex((r) => r.id === id);
  if (i >= 0) rollouts.splice(i, 1);
}

export function playerSync(p: RolloutPlayer): PlayerSync {
  const c = screensById.value.get(p.screenId)?.content;
  if (!c) return 'waiting';
  // Files that failed before a change (remove, pause…) aren't this change's problem: Health Check tracks them.
  if (c.playingRevision !== null && c.playingRevision >= p.revision)
    return c.filesFailed > 0 && !p.change ? 'partial' : 'done';
  if (c.status === 'offline') return p.pairedAt && Date.now() - p.pairedAt < PAIRING_GRACE_MS ? 'waiting' : 'offline';
  if (c.status === 'updating') return 'downloading';
  if (c.status === 'problem') return 'problem';
  if (c.status === 'failing') return 'failing';
  return 'waiting';
}
export const SYNC_RANK: Record<PlayerSync, number> = {
  problem: 0,
  failing: 0,
  offline: 1,
  downloading: 2,
  waiting: 3,
  partial: 4,
  done: 5,
};

export function summary(r: Rollout) {
  const s = r.players.map(playerSync);
  const n = (k: PlayerSync) => s.filter((x) => x === k).length;
  const ready = n('done') + n('partial');
  // Overall progress: a ready player counts 100 %, a downloading one its download %, the others 0.
  const parts = r.players.map((p, i) =>
    s[i] === 'done' || s[i] === 'partial'
      ? 100
      : s[i] === 'downloading'
        ? (screensById.value.get(p.screenId)?.content?.downloadPercent ?? 0)
        : 0,
  );
  const pct = Math.floor(parts.reduce((a, b) => a + b, 0) / Math.max(1, parts.length));
  return {
    total: r.players.length,
    ready,
    pct,
    downloading: n('downloading'),
    waiting: n('waiting'),
    offline: n('offline'),
    failing: n('failing'),
    problem: n('problem') + n('partial') + n('failing'),
    complete: ready === r.players.length,
    parked: ready < r.players.length && ready + n('offline') === r.players.length,
  };
}

let timer: ReturnType<typeof setInterval> | undefined;
let inFlight = false;
async function poll(): Promise<void> {
  if (inFlight || document.visibilityState !== 'visible') return;
  inFlight = true;
  try {
    const ids = rollouts.flatMap((r) => r.players.map((p) => p.screenId));
    screensById.value = new Map((await screensByIds(ids)).map((s) => [s.id, s]));
    for (const r of [...rollouts]) {
      if (r.fresh) {
        delete r.fresh;
        const confirm = confirms.get(r.id);
        confirms.delete(r.id);
        // Every player it went to is offline: nothing to watch now. Say so plainly (they update when they're back)
        // instead of a tray that would only say "behind" and fold away. Applies to every publish and change.
        if (r.players.every((p) => playerSync(p) === 'offline')) {
          dismissRollout(r.id);
          offlineNotice(r, confirm); // says what was done too: one notice, not two
          continue;
        }
        showConfirm(confirm);
      }
      const sum = summary(r);
      if (sum.complete && !r.doneAt) r.doneAt = Date.now();
      // All green: fade out after a few seconds. With missing files it stays until dismissed.
      if (r.doneAt && sum.problem === 0 && Date.now() - r.doneAt > 4_000) dismissRollout(r.id);
      // Only offline players left: they get it when they reconnect (the player list shows them behind). Say so, then
      // stop following — the tray would otherwise wait for days.
      if (sum.parked) r.parkedAt ??= Date.now();
      else delete r.parkedAt;
      if (r.parkedAt && Date.now() - r.parkedAt > 10_000) dismissRollout(r.id);
    }
  } catch {
    /* offline: the next tick retries */
  } finally {
    inFlight = false;
  }
}
export const active = computed(() => rollouts.length > 0);
watch(
  active,
  (on) => {
    clearInterval(timer);
    if (on) {
      void poll();
      timer = setInterval(() => void poll(), 3_000);
    }
  },
  { immediate: true },
);
